import React, { useEffect, useState } from 'react';
import { fastapi } from '../services/api';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan } from '@fortawesome/free-solid-svg-icons';

interface FeatureGuardProps {
  children: React.ReactNode;
  featureKey: string;
}

const FeatureGuard: React.FC<FeatureGuardProps> = ({ children, featureKey }) => {
  const [enabledFeatures, setEnabledFeatures] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const res = await fastapi.get('/tenants/me/features');
        setEnabledFeatures(res.data);
      } catch (e) {
        console.error('Failed to fetch features', e);
        setEnabledFeatures([]);
      } finally {
        setLoading(false);
      }
    };

    if (localStorage.getItem('jwtToken')) {
      fetchFeatures();
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[50vh]">
        <div className="flex space-x-2">
          <span className="w-2.5 h-2.5 bg-blue-400/80 rounded-full animate-bounce shadow-[0_0_8px_rgba(96,165,250,0.8)]" style={{ animationDelay: '0ms', animationDuration: '1s' }}></span>
          <span className="w-2.5 h-2.5 bg-purple-400/80 rounded-full animate-bounce shadow-[0_0_8px_rgba(192,132,252,0.8)]" style={{ animationDelay: '150ms', animationDuration: '1s' }}></span>
          <span className="w-2.5 h-2.5 bg-pink-400/80 rounded-full animate-bounce shadow-[0_0_8px_rgba(244,114,182,0.8)]" style={{ animationDelay: '300ms', animationDuration: '1s' }}></span>
        </div>
      </div>
    );
  }

  if (enabledFeatures && !enabledFeatures.includes(featureKey)) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[70vh] text-center px-4">
        <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6 shadow-inner-glow shadow-inner-glow-white-sm border border-red-500/20">
          <FontAwesomeIcon icon={faBan} className="text-3xl text-red-400 drop-shadow-[0_0_8px_rgba(248,113,113,0.5)]" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">{t('module.disabled.title', 'Módulo deshabilitado')}</h2>
        <p className="text-white/60 max-w-md text-sm leading-relaxed">
          {t('module.disabled.message', 'No tienes acceso a este módulo. Por favor, contacta con el administrador para habilitarlo.')}
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

export default FeatureGuard;
